import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { useRBAC } from '../contexts/RBACContext';
import { useSport } from '../contexts/SportContext';
import { AZURE } from '../services/api';
import { VUKZ } from '../theme/vukzTheme';
import PageLayout from '../components/layout/PageLayout';


// ===== Brand tokens =====

// ===== Helpers =====
function safeStringify(obj) {
  try { return JSON.stringify(obj, null, 2); } catch { return String(obj); }
}

// ===== Sport display names =====
const SPORT_LABELS = {
  swimming: 'Natação',
  football: 'Futebol',
  american_football: 'Futebol Americano',
  triathlon: 'Triatlon',
};

// ===== Suggestion chips by sport =====
const SPORT_CHIPS = {
  football: [
    'Ajustar treino de hoje',
    'Planejar microciclo da semana',
    'Revisar sessão anterior e sugerir correções',
  ],
  american_football: [
    'Distribuir carga por posições',
    'Planejar sessão com contato controlado',
    'Ajustar treino técnico + físico',
  ],
  swimming: [
    'Ajustar volume e intensidade do dia',
    'Sugerir foco técnico (saída/virada/tração)',
    'Planejar semana com recuperação',
  ],
  triathlon: [
    'Balancear carga corrida/ciclismo/natação',
    'Sugerir sessão de força complementar',
    'Checar sinais de fadiga e ajustar',
  ],
};

// ===== Role config =====
const ROLE_CONFIG = {
  TREINADOR: {
    title: 'Vukz Swim Assist',
    subtitle: 'Apoio rápido para decisões de treino, sessão e ajustes.',
    icon: '🏋️',
    initialMessage: 'Posso te ajudar. Qual é o objetivo da sessão e as restrições de hoje?',
  },
  ATLETA: {
    title: 'Vukz Swim Assist',
    subtitle: 'Apoio para rotina, dúvidas rápidas e execução do plano.',
    icon: '🏃',
    initialMessage: 'Posso te ajudar. O que precisa sobre seu treino ou plano de hoje?',
  },
};

const STORAGE_KEY = 'arena_mind_conversations_v1';

const loadConversations = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const generateId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
const nowIso = () => new Date().toISOString();

// NOTE: INITIAL_MESSAGE kept as default; role-aware version injected in component.
const INITIAL_MESSAGE = {
  role: 'assistant',
  content: 'Posso te ajudar. Qual é o objetivo da sessão e as restrições de hoje?',
  ts: nowIso()
};

// ===== COMPONENTE PRINCIPAL =====
const MindPage = () => {
  const { user } = useAuth();
  const { activeRole: currentActiveRole } = useRBAC();
  const { activeSport } = useSport();

  // ===== Role/Sport-aware config =====
  const activeRole = currentActiveRole || 'TREINADOR';
  const roleConf = ROLE_CONFIG[activeRole] || ROLE_CONFIG['TREINADOR'];
  const sportLabel = SPORT_LABELS[activeSport] || activeSport || '';
  const chips = SPORT_CHIPS[activeSport] || SPORT_CHIPS.football;

  // Page-level access is now handled by RouteGuard in App.js

  const [conversations, setConversations] = useState(loadConversations);
  const [activeConvId, setActiveConvId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const listRef = useRef(null);
  const textareaRef = useRef(null);

  // ===== Init conversation =====
  useEffect(() => {
    if (conversations.length === 0) {
      const first = {
        id: generateId(),
        title: 'Nova conversa',
        createdAt: nowIso(),
        updatedAt: nowIso(),
        messages: [INITIAL_MESSAGE]
      };
      setConversations([first]);
      setActiveConvId(first.id);
      return;
    }

    if (!activeConvId) {
      setActiveConvId(conversations[0]?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations.length, activeConvId]);

  // ===== Persist with debounce =====
  const saveTimeoutRef = useRef(null);
  useEffect(() => {
    if (conversations.length === 0) return;

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

    saveTimeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(conversations));
      } catch (e) {
        console.error('[MindPage] Erro ao salvar no localStorage:', e);
      }
    }, 500);

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [conversations]);

  const activeConv = useMemo(() => {
    return conversations.find((c) => c.id === activeConvId) || conversations[0] || null;
  }, [conversations, activeConvId]);

  const messages = activeConv?.messages || [INITIAL_MESSAGE];

  // Filtrar INITIAL_MESSAGE da renderização
  const displayMessages = useMemo(() => {
    return messages.filter((m, idx) => {
      // Não renderizar primeira mensagem assistant padrão
      if (idx === 0 && m.role === 'assistant' && m.content === INITIAL_MESSAGE.content) {
        return false;
      }
      return true;
    });
  }, [messages]);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (listRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    });
  }, []);

  // Auto-resize textarea
  const resizeTextarea = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [input, resizeTextarea]);

  // ===== Actions =====
  const createNewChat = useCallback(() => {
    const newConv = {
      id: generateId(),
      title: 'Nova conversa',
      createdAt: nowIso(),
      updatedAt: nowIso(),
      messages: [INITIAL_MESSAGE]
    };
    setConversations((prev) => [newConv, ...prev]);
    setActiveConvId(newConv.id);
    setError(null);
    setInput('');
    setShowHistory(false);
  }, []);

  const selectConversation = useCallback((convId) => {
    setActiveConvId(convId);
    setError(null);
  }, []);

  const deleteConversation = useCallback(
    (convId) => {
      setConversations((prev) => {
        if (prev.length <= 1) {
          window.alert('Você precisa ter ao menos uma conversa.');
          return prev;
        }
        if (!window.confirm('Deletar esta conversa?')) return prev;

        const next = prev.filter((c) => c.id !== convId);

        if (activeConvId === convId) {
          setActiveConvId(next[0]?.id || null);
        }

        return next;
      });
      setError(null);
    },
    [activeConvId]
  );

  const maybeAutoTitle = useCallback((conv, userText) => {
    if (!conv) return conv;
    if (conv.title && conv.title !== 'Nova conversa') return conv;

    const short = userText.length > 42 ? userText.slice(0, 42).trim() + '…' : userText.trim();
    return { ...conv, title: short };
  }, []);

  const send = useCallback(
    async (text) => {
      const trimmed = (text || '').trim();
      if (!trimmed || isLoading || !activeConvId) return;

      setError(null);
      setIsLoading(true);
      setInput('');

      const userMsg = { role: 'user', content: trimmed, ts: nowIso() };

      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === activeConvId
            ? { ...maybeAutoTitle(conv, trimmed), messages: [...conv.messages, userMsg], updatedAt: nowIso() }
            : conv
        )
      );

      scrollToBottom();

      try {
        // Usar AZURE axios instance ao invés de fetch()
        const { data } = await AZURE.post('/mind/query', { message: trimmed });

        const answer = data.answer || data.message || data.output || safeStringify(data);
        const trace_id = data.trace_id || null;
        
        const assistantMsg = { role: 'assistant', content: answer, trace_id, ts: nowIso() };

        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === activeConvId ? { ...conv, messages: [...conv.messages, assistantMsg], updatedAt: nowIso() } : conv
          )
        );

        scrollToBottom();
      } catch (e) {
        const errorMessage = e?.response?.data?.message || e?.message || 'Erro ao chamar o Arena Mind.';
        const trace_id = e?.response?.data?.trace_id || null;
        setError({ message: errorMessage, trace_id });
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, activeConvId, maybeAutoTitle, scrollToBottom]
  );

  const handleInputChange = useCallback((e) => {
    setInput(e.target.value);
  }, []);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        send(input);
      }
    },
    [input, send]
  );

  // ===== Page gate removed — RouteGuard handles access =====

  const pageTitle = roleConf?.title || 'Vukz Swim Assist';
  const pageSubtitle = roleConf?.subtitle || '';

  return (
    <PageLayout
      header={{
        title: pageTitle,
        subtitle: `${pageSubtitle} • ${activeRole} • ${sportLabel || 'Geral'}`,
        actions: (
          <div className="flex items-center gap-3">
            <div 
              className="px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-2"
              style={{ backgroundColor: '#f3f4f6', color: VUKZ.primary }}
            >
              <span 
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: isLoading ? VUKZ.accentAlt : '#10b981' }}
              />
              {isLoading ? 'Pensando...' : 'Online'}
            </div>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="px-4 py-2 text-sm font-semibold rounded-lg border hover:bg-gray-50 transition"
              style={{ borderColor: '#e5e7eb', color: VUKZ.primary }}
            >
              📋 Histórico
            </button>
            <button
              onClick={createNewChat}
              className="px-4 py-2 text-sm font-semibold rounded-lg transition"
              style={{ backgroundColor: VUKZ.primary, color: 'white' }}
            >
              + Nova
            </button>
          </div>
        ),
      }}
    >

      {/* Chat Card */}
        <div className="bg-white rounded-2xl border shadow-sm overflow-hidden" style={{ borderColor: '#e5e7eb', minHeight: '60vh' }}>
          {/* Mensagens */}
          <div className="flex-1 overflow-y-auto p-6" ref={listRef} style={{ maxHeight: '60vh' }}>
            <div className="space-y-4">
              {displayMessages.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <p className="text-lg">Inicie uma conversa...</p>
                </div>
              )}
              
              {displayMessages.map((m, idx) => {
                const isUser = m.role === 'user';
                return (
                  <div key={idx} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className="max-w-[75%] px-4 py-3 rounded-2xl border-2"
                      style={{ borderColor: VUKZ.border, backgroundColor: 'white', color: VUKZ.primary }}
                    >
                      <div className="whitespace-pre-wrap">{m.content}</div>
                      {m.trace_id && (
                        <div className="mt-2 pt-2 border-t text-xs text-gray-500">
                          trace: {m.trace_id}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {error && (
                <div className="border-2 rounded-2xl p-4 bg-red-50" style={{ borderColor: '#fca5a5' }}>
                  <div className="font-bold text-red-900">Erro</div>
                  <div className="mt-1 text-sm text-red-800">{error.message}</div>
                </div>
              )}
            </div>
          </div>

          {/* Input Area (dentro do card) */}
          <div className="border-t p-4" style={{ borderColor: '#e5e7eb' }}>
            {/* Suggestion chips */}
            {displayMessages.length === 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {chips.map((chip, idx) => (
                  <button
                    key={idx}
                    onClick={() => send(chip)}
                    disabled={isLoading}
                    className="px-3 py-1.5 text-xs font-semibold rounded-full border-2 hover:shadow-sm transition disabled:opacity-50"
                    style={{ borderColor: VUKZ.border, color: VUKZ.primary, backgroundColor: '#f9fafb' }}
                  >
                    {chip}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={onKeyDown}
                placeholder="Descreva a sessão, objetivo e restrições…"
                disabled={isLoading}
                className="flex-1 px-4 py-3 border-2 rounded-lg outline-none resize-none"
                style={{ 
                  borderColor: VUKZ.border, 
                  color: VUKZ.primary,
                  minHeight: '48px',
                  maxHeight: '120px'
                }}
                rows={1}
              />
              <button
                onClick={() => send(input)}
                disabled={isLoading || !input.trim()}
                className="px-6 py-3 rounded-lg font-semibold transition disabled:opacity-50"
                style={{
                  backgroundColor: isLoading || !input.trim() ? '#e5e7eb' : VUKZ.primary,
                  color: isLoading || !input.trim() ? '#6b7280' : 'white'
                }}
              >
                {isLoading ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
            <div className="mt-2 text-xs text-gray-400 text-center">
              Enter envia · Shift+Enter nova linha
            </div>
          </div>
        </div>

      {/* Histórico (Seção colapsável abaixo do chat) */}
      {showHistory && (
          <div className="bg-white rounded-2xl border shadow-sm p-6 mt-6" style={{ borderColor: '#e5e7eb' }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: VUKZ.primary }}>
              Histórico de Conversas
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {conversations.map((conv) => {
                const active = conv.id === activeConvId;
                return (
                  <div
                    key={conv.id}
                    onClick={() => selectConversation(conv.id)}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition hover:shadow-md group ${
                      active ? 'shadow-md' : ''
                    }`}
                    style={{ 
                      borderColor: active ? VUKZ.primary : '#e5e7eb',
                      backgroundColor: active ? '#f3f4f6' : 'white'
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate" style={{ color: VUKZ.primary }}>
                          {conv.title || 'Conversa'}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {new Date(conv.updatedAt).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {conv.messages.length} mensagens
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteConversation(conv.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 p-1"
                        title="Deletar"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
      )}
    </PageLayout>
  );
};

export default MindPage;
